/**
 * Solanaland
 *
 * Author: Venus Lumanglas
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { Button, Card, Input, Table } from "core/components";
import { InventoryItemFormData, SupplierFormData } from "core/schema";
import formatter from "core/formatter";
import pluralize from "pluralize";
import { api } from "core/api";
import { remove_supplier_admin_inventory_item_path } from "routes";
import InventoryItemSupplierForm from "./InventoryItemSupplierForm";

type InventoryItemSuppliersFormProps = {
  inventory_item: InventoryItemFormData;
  inventoryItemSuppliers: Array<Record<string, unknown>>;
  setInventoryItemSuppliers: React.Dispatch<Array<Record<string, unknown>>>;
  suppliers: Array<SupplierFormData>;
  credit_terms: Array<Record<string, unknown>>;
};

const InventoryItemSuppliersForm = ({
  inventory_item,
  inventoryItemSuppliers,
  setInventoryItemSuppliers,
  suppliers,
  credit_terms
}: InventoryItemSuppliersFormProps): JSX.Element => {
  const [showSupplierModal, setShowSupplierModal] = React.useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = React.useState(false);
  const [currentItemSupplier, setCurrentItemSupplier] = React.useState(null);
  const table = React.useRef(null);

  const addInventoryItemSupplier = (supplier) => {
    const foundIndex = inventoryItemSuppliers.findIndex((inventoryItemSupplier) => inventoryItemSupplier.id == supplier.id);
    const preferredSupplierIndex = inventoryItemSuppliers.findIndex((inventoryItemSupplier) => inventoryItemSupplier.preferred);

    // Check if it is updated supplier
    if (foundIndex != -1) {
      // Check if updated supplier is preferred and if there is already preferred supplier,
      // then set the already preferred supplier to false
      if (supplier.preferred && preferredSupplierIndex != -1) inventoryItemSuppliers[preferredSupplierIndex].preferred = false;

      inventoryItemSuppliers[foundIndex] = supplier;
      setInventoryItemSuppliers([...inventoryItemSuppliers]);
    } else {
      // Check if added supplier is preferred and if there is already preferred supplier,
      // then set the already preferred supplier to false
      if (supplier.preferred && preferredSupplierIndex != -1) inventoryItemSuppliers[preferredSupplierIndex].preferred = false;

      setInventoryItemSuppliers([...inventoryItemSuppliers, supplier]);
    }
  };

  const removeInventoryItemSupplier = (supplier) => {
    setInventoryItemSuppliers(inventoryItemSuppliers.filter((inventoryItemSupplier) => inventoryItemSupplier.id != supplier.id));
  };

  const remove = async () => {
    try {
      await api.deleteItem(remove_supplier_admin_inventory_item_path({ id: inventory_item.id }), {
        data: { item_supplier: { id: currentItemSupplier?.id } }
      });

      removeInventoryItemSupplier(currentItemSupplier);
      setShowDeleteConfirmationModal(!showDeleteConfirmationModal);
    } catch (error) {
      console.error(error);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        Header: "Preferred",
        accessor: "preferred",
        style: {
          width: "80px"
        },
        Cell: (props) => {
          if (props.value)
            return (
              <div className="text-center">
                <span className="badge badge--pill badge--success">Preferred</span>
              </div>
            );
          return <></>;
        }
      },
      {
        id: 1,
        Header: "Supplier Code",
        accessor: "supplier.code",
        Cell: (props) => {
          return (
            <div className="text-center">
              <span>{props.value}</span>
            </div>
          );
        }
      },
      {
        id: 2,
        Header: "Supplier",
        accessor: "supplier.name",
        Cell: (props) => {
          return (
            <div data-toggle="tooltip" title={props.value}>
              <span>{props.value}</span>
            </div>
          );
        }
      },
      {
        Header: "Tax Category",
        accessor: "supplier.tax_category",
        Cell: (props) => {
          return (
            <div className="text-center">
              <span>{I18n.t(`activerecord.attributes.supplier.tax_categories.${props.value}`)}</span>
            </div>
          );
        }
      },
      {
        Header: "CWT Rate",
        accessor: "supplier.withholding_tax_rate",
        Cell: (props) => {
          return (
            <div className="text-center">
              <span>{props.value}</span>
            </div>
          );
        }
      },
      {
        Header: "Lead Time",
        accessor: "lead_time",
        Cell: (props) => {
          return (
            <div className="text-center">
              <span>{pluralize("Day", props.value, true)}</span>
            </div>
          );
        }
      },
      {
        Header: "Standard Price",
        accessor: "standard_price",
        Cell: (props) => {
          return (
            <div className="text-right">
              <span>{formatter.format(props.value)}</span>
            </div>
          );
        }
      },
      {
        id: "current_price",
        Header: "Current Price",
        accessor: "current_price",
        Cell: (props) => {
          return (
            <div className="text-right">
              <span>{formatter.format(props.value)}</span>
            </div>
          );
        }
      },
      {
        Header: "Min. Order Quantity",
        accessor: "min_order_quantity",
        Cell: (props) => {
          return (
            <div className="text-center">
              <span>{props.value}</span>
            </div>
          );
        }
      },
      {
        Header: "Credit Term",
        accessor: "credit_term.code",
        Cell: (props) => {
          return (
            <div className="text-center">
              <span>{props.value}</span>
            </div>
          );
        }
      },
      {
        id: "actions",
        style: {
          width: "70px"
        },
        Cell: (props) => {
          return (
            <>
              <span title="Edit Supplier" data-bs-toggle="tooltip">
                <a
                  className="button button--action button--action-success"
                  onClick={() => {
                    setCurrentItemSupplier(props.row.original);
                    setShowSupplierModal(!showSupplierModal);
                  }}
                >
                  <div className="svg-icon h6">
                    <svg className="icon icon-edit ">
                      <use xlinkHref="#edit"></use>
                    </svg>
                  </div>
                </a>
              </span>
              <span title="Delete Supplier" data-bs-toggle="tooltip">
                <a
                  className="button button--action button--action-danger"
                  onClick={() => {
                    setCurrentItemSupplier(props.row.original);
                    setShowDeleteConfirmationModal(!showDeleteConfirmationModal);
                  }}
                >
                  <div className="svg-icon h6">
                    <svg className="icon icon-delete ">
                      <use xlinkHref="#delete"></use>
                    </svg>
                  </div>
                </a>
              </span>
            </>
          );
        }
      }
    ],
    []
  );

  return (
    <React.StrictMode>
      <Card.Body>
        <Container fluid={true}>
          <Row>
            <Col>
              <Input placeholder="Search for Supplier Code" onChange={(e) => table.current.setFilter(1, e.target.value)} />
            </Col>
            <Col>
              <Input placeholder="Search for Supplier Name" onChange={(e) => table.current.setFilter(2, e.target.value)} />
            </Col>
            <Col>
              <Button
                className="button--brand text-right float-end mb-2"
                title="Add Supplier"
                onClick={() => {
                  setCurrentItemSupplier(null);
                  setShowSupplierModal(!showSupplierModal);
                }}
              />
            </Col>
          </Row>
          <Row>
            {inventoryItemSuppliers.length > 0 ? (
              <Table columns={columns} data={inventoryItemSuppliers} ref={table} />
            ) : (
              <div className="text-center mt-4 mb-4">No suppliers yet.</div>
            )}
          </Row>
        </Container>
      </Card.Body>
      <InventoryItemSupplierForm
        inventory_item={inventory_item}
        suppliers={
          !currentItemSupplier?.id
            ? suppliers.filter(
                (supplier) => !inventoryItemSuppliers.find((inventoryItemSupplier) => supplier.id == inventoryItemSupplier?.supplier?.id)
              )
            : suppliers
        }
        credit_terms={credit_terms}
        currentItemSupplier={currentItemSupplier}
        show={showSupplierModal}
        onClose={() => setShowSupplierModal(!showSupplierModal)}
        onSave={(inventoryItemSupplier) => {
          addInventoryItemSupplier(inventoryItemSupplier);
          setShowSupplierModal(!showSupplierModal);
        }}
      />
      <Modal dialogClassName="modal-dialog-centered" show={showDeleteConfirmationModal}>
        <Modal.Header>{`Remove supplier: ${currentItemSupplier?.supplier?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to remove supplier ${currentItemSupplier?.supplier?.name}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowDeleteConfirmationModal(!showDeleteConfirmationModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button className="button--danger" icon="save" title="Yes, remove supplier." onClick={() => remove()} />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default InventoryItemSuppliersForm;
